import {SeriesType, Site} from 'src/lib/api/project';
import {AggregationMethod, RateVolumeMode, TimeAggregation} from 'src/lib/api/series';
import {diffDays} from 'src/lib/dates';
import * as tw from 'src/lib/tailwindClasses';

import {recordEvent} from './Analytics';
import {useTranslations} from './TranslationsContext';

type TimeAggregationKey = 'hourly' | '6-hourly' | 'daily';
export const TIME_AGGREGATIONS: {[key in TimeAggregationKey]: TimeAggregation} = {
  hourly: '1H',
  '6-hourly': '6H',
  daily: '1D',
};

const MAX_DAY_DIFF_TIME_AGGREGATION: Partial<Record<TimeAggregation, number>> = {
  '1H': 180,
  '6H': 360,
};

export type AggState = {
  timeAggregation: TimeAggregation;
  aggregationMethod: AggregationMethod;
  rateVolumeMode: RateVolumeMode;
};

export function checkTimeAggregationEnabled(
  timeAgg: TimeAggregation,
  date1: Date,
  date2: Date
): boolean {
  const diff = diffDays(date1, date2);
  const maxDiff = MAX_DAY_DIFF_TIME_AGGREGATION[timeAgg];
  if (maxDiff) {
    return diff < maxDiff;
  }
  return true;
}

export default function AggOptions({
  site,
  agg,
  dateRange,
  updateAgg,
  showLevelOption = true,
  analyticsSection = 'short_term',
}: {
  site: Site;
  agg: AggState;
  dateRange: [Date, Date];
  updateAgg: (update: Partial<AggState>) => void;
  showLevelOption?: boolean;
  analyticsSection?: string;
}) {
  const {t} = useTranslations();
  return (
    <>
      <select
        className={tw.select}
        value={agg.rateVolumeMode}
        onChange={(event) => {
          const newMode = event.target.value as RateVolumeMode;
          updateAgg({rateVolumeMode: newMode});
          recordEvent('aggregation_option_changed', {
            section: analyticsSection,
            option: 'rate_volume_mode',
            value: newMode,
          });
        }}
      >
        <option value="rate">{t('rate')}</option>
        <option value="volume">{t('volume')}</option>
        <option value="cumulative-volume">{t('cumulative-volume')}</option>
        <option value="cumulative-volume-from-now">{t('cumulative-volume-from-now')}</option>
        {showLevelOption && site.seriesTypes.includes(SeriesType.Level) && (
          <option value="level">{t('level')}</option>
        )}
      </select>
      <select
        className={tw.select}
        value={agg.timeAggregation}
        onChange={(event) => {
          const newAgg = event.target.value as TimeAggregation;
          updateAgg({timeAggregation: newAgg});
          recordEvent('aggregation_option_changed', {
            section: analyticsSection,
            option: 'time_aggregation',
            value: newAgg,
          });
        }}
      >
        {Object.keys(TIME_AGGREGATIONS).map((aggKey) => {
          const timeAgg = TIME_AGGREGATIONS[aggKey as TimeAggregationKey];
          return (
            <option
              key={timeAgg}
              value={timeAgg}
              disabled={!checkTimeAggregationEnabled(timeAgg, dateRange[0], dateRange[1])}
            >
              {t(aggKey as TimeAggregationKey)}
            </option>
          );
        })}
      </select>
      {(agg.timeAggregation === '1D' || agg.timeAggregation === '6H') && (
        <select
          className={tw.select}
          value={agg.aggregationMethod}
          onChange={(e) => {
            const newMethod = e.target.value as AggregationMethod;
            updateAgg({
              aggregationMethod: newMethod,
            });
            recordEvent('aggregation_option_changed', {
              section: analyticsSection,
              option: 'aggregation_method',
              value: newMethod,
            });
          }}
        >
          <option value="max">{t('max')}</option>
          <option value="mean">{t('mean')}</option>
          <option value="min">{t('min')}</option>
        </select>
      )}
    </>
  );
}
